import React, { useState } from "react";
import { FiExternalLink, FiFileText } from "react-icons/fi";
import { Modal } from "../Modal";
import { resolveStorageUrl } from "../../Helper/storage";

/**
 * Valeurs MÉDIA dans les diffs et le journal des changements : une valeur qui pointe une image
 * stockée se rend en VIGNETTE (clic = plein écran en modale) ; un document (PDF, Office) se rend
 * en bouton qui ouvre une modale d'aperçu. Partagé par ChangeDiffViewer (blocs) et ChangeJournal.
 */

export type MediaKind = "image" | "document";

export interface MediaRef {
	kind: MediaKind;
	url: string;
	/** Nom de fichier court, pour le libellé et le titre de la modale. */
	name: string;
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|avif|bmp)(\?.*)?$/i;
const DOC_EXT = /\.(pdf|docx?|xlsx?|pptx?)(\?.*)?$/i;

/** Une chaîne est un média si elle ressemble à un chemin/URL et porte une extension connue. */
export const detectMedia = (value?: string | null): MediaRef | null => {
	if (!value) return null;
	const v = value.trim();
	if (v.length === 0 || v.length > 600 || /\s/.test(v)) return null;
	const isPathLike = /^(https?:\/\/|\/)/i.test(v) || /^(images|publications)\//i.test(v);
	if (!isPathLike) return null;
	const kind: MediaKind | null = IMAGE_EXT.test(v) ? "image" : DOC_EXT.test(v) ? "document" : null;
	if (!kind) return null;
	const url = resolveStorageUrl(v);
	if (!url) return null;
	const name = v.split("/").pop()?.split("?")[0] ?? v;
	return { kind, url, name };
};

/** Rendu inline d'une valeur média + sa modale d'aperçu. */
export const MediaValue: React.FC<{
	media: MediaRef;
	/** compact : vignette basse pour les lignes du journal d'audit. */
	compact?: boolean;
}> = ({ media, compact = false }) => {
	const [open, setOpen] = useState(false);

	return (
		<>
			{media.kind === "image" ? (
				<button
					type="button"
					onClick={() => setOpen(true)}
					className="inline-flex shrink-0 cursor-zoom-in rounded-md border border-slate-200 bg-white p-0.5 transition hover:border-slate-300"
					title={`Agrandir ${media.name}`}
					data-testid="media-thumb"
				>
					<img
						src={media.url}
						alt={media.name}
						className={`${compact ? "h-8 max-w-[96px]" : "h-14 max-w-[180px]"} rounded object-cover`}
						loading="lazy"
					/>
				</button>
			) : (
				<button
					type="button"
					onClick={() => setOpen(true)}
					className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
					title={`Ouvrir ${media.name}`}
					data-testid="media-doc"
				>
					<FiFileText className="h-3 w-3 shrink-0" /> {media.name}
				</button>
			)}

			<Modal show={open} onHide={() => setOpen(false)} size="xl" centered>
				<Modal.Header closeButton>
					<Modal.Title>{media.name}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{media.kind === "image" ? (
						<img src={media.url} alt={media.name} className="mx-auto max-h-[72vh] max-w-full rounded-lg object-contain" />
					) : (
						<iframe src={media.url} title={media.name} className="h-[70vh] w-full rounded-lg border border-slate-200" />
					)}
				</Modal.Body>
				<Modal.Footer className="justify-between">
					<a
						href={media.url}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1 text-xs font-semibold text-[#005CA9] hover:underline"
					>
						<FiExternalLink className="h-3 w-3" /> Ouvrir dans un nouvel onglet
					</a>
					<button
						type="button"
						onClick={() => setOpen(false)}
						className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
					>
						Fermer
					</button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default MediaValue;
